import { type FC, type ReactNode } from 'react';
import { useIntl } from 'react-intl';

// MUI
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

// common
import LogoAvatar from 'common/components/LogoAvatar';
import MuiLoading from 'common/components/MuiLoading';

// screen
import { useTabsData } from 'modules/suppliers/detail/context/useTabsData';
import BackToSuppliersPageButton from 'modules/suppliers/detail/components/BackToSuppliersPageButton';
import { buildFullAddress } from 'modules/suppliers/helpers/supplierAddress';

/** Mã CĐS hiển thị thành 3 ô rời (tiền tố - phần giữa - hậu tố), như thiết kế. */
const codeParts = (code?: string): [string, string, string] => {
  const parts = (code || '').split('-');
  if (parts.length <= 1) return [code || '', '', ''];
  return [parts[0], parts.slice(1, -1).join('-'), parts[parts.length - 1]];
};

// App dùng breakpoint tuỳ biến (mobile/tablet/laptop/desktop), KHÔNG có xs/md mặc định.
const grid2 = {
  display: 'grid',
  gridTemplateColumns: { mobile: '1fr', laptop: '1fr 1fr' },
  gap: 2,
} as const;

const boxSx = {
  bgcolor: 'action.hover',
  borderRadius: 1,
  px: 1.5,
  py: 1,
  minHeight: 40,
  display: 'flex',
  alignItems: 'center',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
} as const;

/**
 * Tab "Thông tin chung" của chi tiết HSNL NCC ở Quick Search (thiết kế node 6885-26427).
 *
 * Chỉ 7 trường: Tên HSNL NCC, Logo, Người đại diện, SĐT | Mã CĐS HSNL, Email, Địa chỉ.
 * KHÔNG có Mã QR (khác QR NCC TPAT) và KHÔNG tách tỉnh/xã/quận cũ thành ô riêng — địa chỉ là MỘT ô
 * đầy đủ, giống hệt cột địa chỉ ngoài danh sách.
 *
 * Hai card tham chiếu nằm ở SupplierRefCards, ngoài TabsView.
 */
const QuickSearchDetail: FC = () => {
  const { formatMessage } = useIntl();
  const { generalDetails } = useTabsData();

  if (!generalDetails) return <MuiLoading />;

  const g = generalDetails;
  const [c1, c2, c3] = codeParts(g.operationCode);
  const label = (id: string) => formatMessage({ id: `quickSearch.detailFields.${id}` });

  const Field = ({
    labelId,
    value,
    required,
  }: {
    labelId: string;
    value?: ReactNode;
    required?: boolean;
  }) => (
    <Box>
      <Typography sx={{ fontSize: 13, mb: 0.5 }}>
        {label(labelId)}
        {required && (
          <Box component="span" sx={{ color: 'error.main' }}>
            {' *'}
          </Box>
        )}
      </Typography>
      <Box sx={boxSx}>{value || ''}</Box>
    </Box>
  );

  return (
    <Box>
      <Box sx={grid2}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Field labelId="hsnlName" value={g.name} required />
          <Box>
            <Typography sx={{ fontSize: 13, mb: 0.5 }}>
              {label('logo')}
              <Box component="span" sx={{ color: 'error.main' }}>
                {' *'}
              </Box>
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <LogoAvatar src={g.logo} size={120} alt={g.name || ''} />
            </Box>
          </Box>
          <Field labelId="representative" value={g.representative} />
          <Field labelId="phone" value={g.phone} required />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography sx={{ fontSize: 13, mb: 0.5 }}>
              {label('maCdsHsnl')}
              <Box component="span" sx={{ color: 'error.main' }}>
                {' *'}
              </Box>
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ ...boxSx, flex: 1 }}>{c1}</Box>
              <span>-</span>
              <Box sx={{ ...boxSx, flex: 2 }}>{c2}</Box>
              <span>-</span>
              <Box sx={{ ...boxSx, flex: 1 }}>{c3}</Box>
            </Box>
          </Box>
          <Field labelId="email" value={g.email} />
          <Field
            labelId="address"
            value={buildFullAddress({
              addressDetail: g.addressDetail,
              wardName: g.wardName,
              cityName: g.cityName,
            })}
            required
          />
        </Box>
      </Box>

      <BackToSuppliersPageButton />
    </Box>
  );
};

export default QuickSearchDetail;
